// @ts-nocheck

import React, { useEffect, useState } from "react";
import SoftBox from "../../components/common/softBox";
import DashboardNavbar from "../../components/navbars/dashboardNavbar";
import SoftTypography from "../../components/common/softTypography";
import SoftInput from "../../components/common/softInput";
import SoftButton from "../../components/common/softButton";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import { getSapConfig, updateSapConfig } from "../../features/sapConfig";
import { sendTestEmailQuery } from "../../api/sapConfigApi";
import { toast } from "react-toastify";
import { Grid, MenuItem, Select, FormControl } from "@mui/material";

const EMPTY_FORM = {
  smtp_host: "",
  smtp_port: "",
  smtp_secure: false,
  smtp_user: "",
  smtp_password: "",
  email_from_name: "",
  email_from_address: "",
  sms_api_key: "",
  sms_user_name: "",
  sms_sender_id: "",
};

const SMTP_FIELDS = [
  { name: "smtp_host", label: "SMTP Host" },
  { name: "smtp_port", label: "SMTP Port", type: "number" },
  { name: "smtp_user", label: "SMTP User" },
  { name: "smtp_password", label: "SMTP Password", type: "password" },
  { name: "email_from_name", label: "Email From Name" },
  { name: "email_from_address", label: "Email From Address" },
];

const SMS_FIELDS = [
  { name: "sms_api_key", label: "SMS API Key" },
  { name: "sms_user_name", label: "SMS Username" },
  { name: "sms_sender_id", label: "SMS Sender ID" },
];

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SoftBox mt={3} mb={1}>
    <SoftTypography variant="h6" fontWeight="bold" textAlign="left">
      {children}
    </SoftTypography>
  </SoftBox>
);

const NotificationConfig: React.FC = () => {
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [testEmail, setTestEmail] = useState("");
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state: any) => state.sapConfig);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "smtp_port" ? (value === "" ? "" : parseInt(value, 10)) : value,
    }));
  };

  useEffect(() => {
    dispatch(getSapConfig());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      setFormData((prev) => ({ ...prev, ...data }));
    }
  }, [data]);

  const handleSubmit = () => {
    dispatch(updateSapConfig({ data: formData })).then((resultAction) => {
      if (updateSapConfig.rejected.match(resultAction)) {
        console.error(resultAction.payload || resultAction.error);
      } else {
        toast.success("Notification configuration updated successfully");
      }
    });
  };

  const handleSendTestEmail = () => {
    if (!testEmail) {
      toast.error("Enter an email address");
      return;
    }
    sendTestEmailQuery(testEmail)
      .then((res) => {
        if (res?.status === false) {
          toast.error(res?.message || "Failed to send test email");
        } else {
          toast.success("Test email sent successfully");
        }
      })
      .catch(() => toast.error("Failed to send test email"));
  };

  const renderInput = (field: {
    name: string;
    label: string;
    type?: string;
  }) => (
    <Grid item md={4} xs={12} key={field.name}>
      <SoftBox mb={2}>
        <SoftBox mb={1} ml={0.5}>
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            {field.label}
          </SoftTypography>
        </SoftBox>
        <SoftInput
          type={field.type || "text"}
          name={field.name}
          placeholder={`Enter ${field.label}`}
          value={formData[field.name]}
          onChange={handleChange}
        />
      </SoftBox>
    </Grid>
  );

  return (
    <SoftBox className="language-box">
      <DashboardNavbar />
      <SoftBox my={2}>
        <SoftTypography variant="h4" fontWeight="bold" textAlign="left">
          Notification Configuration
        </SoftTypography>
      </SoftBox>

      <SoftBox component="form" role="form">
        <SectionTitle>SMTP Email</SectionTitle>
        <Grid container spacing={2}>
          {SMTP_FIELDS.map(renderInput)}
          <Grid item md={4} xs={12}>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  SMTP Secure
                </SoftTypography>
              </SoftBox>
              <FormControl fullWidth>
                <Select
                  name="smtp_secure"
                  value={formData.smtp_secure ? "true" : "false"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      smtp_secure: e.target.value === "true",
                    }))
                  }
                  size="small"
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </SoftBox>
          </Grid>
        </Grid>

        <SoftBox mt={2} mb={3} display="flex" alignItems="flex-end" gap={2}>
          <SoftBox sx={{ width: { xs: "100%", md: "40%" } }}>
            <SoftBox mb={1} ml={0.5}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
              >
                Send Test Email To
              </SoftTypography>
            </SoftBox>
            <SoftInput
              type="email"
              name="testEmail"
              placeholder="Enter recipient email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </SoftBox>
          <SoftBox mb={0.5}>
            <SoftButton
              variant="outlined"
              color="dark"
              type="button"
              onClick={handleSendTestEmail}
            >
              Send Test Email
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <SectionTitle>SMS Gateway</SectionTitle>
        <Grid container spacing={2}>
          {SMS_FIELDS.map(renderInput)}
        </Grid>

        <SoftBox mt={4} mb={1}>
          <SoftButton
            variant="gradient"
            color="final"
            fullWidth
            onClick={handleSubmit}
          >
            Save
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
};

export default NotificationConfig;
